import { IconDownload, IconEye } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Badge, Group, IconAction, Text, ThemeIcon } from "@/components/ui";
import { toastErrorApi } from "@/lib/toast";
import type { DocumentoVinculado } from "@/lib/types";
import { documentosApi } from "../services/documentos.api";
import { badgeColorCategoria, esPrevisualizable, extensionDe, labelCategoria, nodoIconoPorExtension } from "../utils/documento-helpers";
import type { DocumentoPreview } from "./DocumentoPreviewContent";
import { DocumentoPreviewModal } from "./DocumentoPreviewModal";

/**
 * Tile (grid cell) de un documento dentro del `DocumentosSection` ·
 * encapsula su propio state de preview (blob + modal abierto), las dos
 * mutations (descargar, previsualizar) y el cleanup del blob URL al
 * desmontar.
 *
 * Se llama "Tile" en lugar de "Row" porque visualmente es una cuadrícula
 * (grid de 1/2/3 columnas según viewport) y para evitar la colisión de
 * nombres con el `DocumentoRow` del paso de upload del wizard.
 */
export function DocumentoTile({ solicitudId, doc, categoria }: { solicitudId: string; doc: DocumentoVinculado; categoria: string }) {
  const [preview, setPreview] = useState<DocumentoPreview | null>(null);
  const [opened, setOpened] = useState(false);

  const previsualizar = useMutation({
    mutationFn: () => documentosApi.previsualizar(solicitudId, doc.documentoId),
    onSuccess: (data) => {
      setPreview(data);
      setOpened(true);
    },
    onError: toastErrorApi,
  });

  const descargar = useMutation({
    mutationFn: () => documentosApi.descargar(solicitudId, doc.documentoId, doc.nombreOriginal),
    onError: toastErrorApi,
  });

  const cerrarPreview = () => {
    setOpened(false);
    if (preview) {
      window.URL.revokeObjectURL(preview.blobUrl);
      setPreview(null);
    }
  };

  // Cleanup al desmontar para no filtrar blob URLs si el padre se descarga.
  useEffect(() => {
    return () => {
      if (preview) window.URL.revokeObjectURL(preview.blobUrl);
    };
  }, [preview]);

  const ext = extensionDe(doc.nombreOriginal);
  const tipoLabel = ext ? ext.toUpperCase() : "—";
  const previewable = esPrevisualizable(ext);

  return (
    <>
      <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-border p-2">
        <Group gap={8} wrap="nowrap" align="center" className="min-w-0">
          <ThemeIcon variant="filled" color="multiva" size="md" className="shrink-0">
            {nodoIconoPorExtension(ext, 14, 1.6)}
          </ThemeIcon>
          <div className="min-w-0 flex-1">
            <Text size="sm" fw={500} truncate title={doc.nombreOriginal}>
              {doc.nombreOriginal}
            </Text>
            <Group gap={6} wrap="nowrap" className="mt-0.5">
              <Badge variant="light" color={badgeColorCategoria(categoria)} size="xs" uppercase={false}>
                {labelCategoria(categoria)}
              </Badge>
              <Badge variant="outline" size="xs">
                {tipoLabel}
              </Badge>
              <Text size="xs" c="dimmed" truncate>
                {new Date(doc.vinculadoAt).toLocaleString("es-MX", { day: "2-digit", month: "short" })}
              </Text>
            </Group>
          </div>
        </Group>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <IconAction label={previewable ? "Previsualizar" : "Sin vista previa"} variant="subtle" size="sm" disabled={!previewable} loading={previsualizar.isPending} onClick={() => previsualizar.mutate()}>
            <IconEye size={14} stroke={1.7} />
          </IconAction>
          <IconAction label="Descargar" variant="subtle" size="sm" loading={descargar.isPending} onClick={() => descargar.mutate()}>
            <IconDownload size={14} stroke={1.7} />
          </IconAction>
        </Group>
      </div>

      <DocumentoPreviewModal opened={opened} preview={preview} nombre={doc.nombreOriginal} descargando={descargar.isPending} onCerrar={cerrarPreview} onDescargar={() => descargar.mutate()} />
    </>
  );
}
